import { FileSpreadsheet, Trash2, UploadCloud } from "lucide-react";
import React, { useRef, useState } from "react";
import { getErrorDetails } from "../../../../app/appUtils";
import AppButton from "../../../../components/ui/AppButton";
import { Modal } from "../../../../components/ui/Modal";
import { useToast } from "../../../../hooks/useToast";
import { DispatchImportResult } from "../../@types";
import { useImportDispatchesMutation } from "../../dispatchApiSlice";
import ImportResultPanel from "./ImportResultPanel";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const MAX_FILE_SIZE_MB = 20;

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

interface DispatchImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Called after every upload attempt that reached the server, so the
  // history list can refresh.
  onImported: () => void;
  onReverse: (importBatchId: string, fileName?: string) => void;
}

const DispatchImportModal: React.FC<DispatchImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
  onReverse,
}) => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState<DispatchImportResult | null>(null);
  const [resultFileName, setResultFileName] = useState<string>();

  const [importDispatches, { isLoading: isImporting }] =
    useImportDispatchesMutation();

  const selectFile = (selected: File | null) => {
    if (!selected) {
      setFile(null);
      return;
    }

    const name = selected.name.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      toast.warning({
        title: "Unsupported file",
        message: `Please upload one of: ${ACCEPTED_EXTENSIONS.join(", ")}`,
      });
      return;
    }

    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.warning({
        title: "File too large",
        message: `Files must be ${MAX_FILE_SIZE_MB} MB or smaller.`,
      });
      return;
    }

    setFile(selected);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetForAnotherFile = () => {
    clearFile();
    setResult(null);
    setResultFileName(undefined);
  };

  const handleClose = () => {
    if (isImporting) return;
    onClose();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    selectFile(e.dataTransfer.files?.[0] || null);
  };

  const handleImport = async () => {
    if (!file || isImporting) return;

    try {
      const response = await importDispatches(file).unwrap();
      const data = response.data;
      setResult(data);
      setResultFileName(file.name);
      onImported();

      if (data?.journeysImported > 0) {
        toast.success({
          title: "Import complete",
          message:
            response.message ||
            `${data.journeysImported} journey(s) imported.`,
        });
      } else {
        toast.warning({
          title: "Nothing imported",
          message:
            response.message || "No new journeys were found in this file.",
        });
      }
    } catch (err) {
      // A failed import can still carry row-level errors worth showing.
      const failedResult = (err as any)?.data?.response?.data as
        | DispatchImportResult
        | undefined;
      if (failedResult) {
        setResult(failedResult);
        setResultFileName(file.name);
        onImported();
      }

      toast.error({
        title: "Import failed",
        message:
          getErrorDetails(err).message ||
          "Failed to import dispatches. Please try again.",
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={result ? "Import result" : "Dispatch import"}
      className="max-w-4xl"
    >
      <div className="p-4 sm:p-6 space-y-5">
        {result ? (
          <>
            <ImportResultPanel
              embedded
              result={result}
              fileName={resultFileName}
              onReverse={() => onReverse(result.importBatchId, resultFileName)}
            />
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <AppButton
                variant="secondary"
                appearance="outline"
                size="md"
                onClick={resetForAnotherFile}
              >
                Import another file
              </AppButton>
              <AppButton size="md" onClick={onClose}>
                Done
              </AppButton>
            </div>
          </>
        ) : (
          <>
            <p className="text-[13px] text-pColor">
              Upload a loading sheet spreadsheet to create dispatches in bulk.
              Rows that were already imported are skipped automatically.
            </p>

            <div
              onClick={() => !isImporting && fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-gray-300 hover:border-primary hover:bg-gray-50"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(",")}
                className="hidden"
                onChange={(e) => selectFile(e.target.files?.[0] || null)}
              />
              <UploadCloud className="w-10 h-10 text-primary" />
              <p className="text-[14px] text-hColor">
                <span className="font-semibold text-primary">
                  Click to upload
                </span>{" "}
                or drag and drop
              </p>
              <p className="text-[12px] text-pColor">
                {ACCEPTED_EXTENSIONS.join(", ")} · up to {MAX_FILE_SIZE_MB} MB
              </p>
            </div>

            {file && (
              <div className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <FileSpreadsheet className="w-8 h-8 text-green-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-hColor break-all">
                      {file.name}
                    </p>
                    <p className="text-[12px] text-pColor">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearFile}
                  disabled={isImporting}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  aria-label="Remove file"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <AppButton
                variant="secondary"
                appearance="outline"
                size="md"
                onClick={handleClose}
                disabled={isImporting}
              >
                Cancel
              </AppButton>
              <AppButton
                size="md"
                icon={<UploadCloud className="w-4 h-4" />}
                onClick={handleImport}
                isLoading={isImporting}
                disabled={!file}
              >
                Import
              </AppButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DispatchImportModal;
